import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Select, Box, Text, Loader, Group, ActionIcon, Tooltip } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { EntityBrowserDialog } from '../dialogs';
import { useFieldOptionsForEntityType } from '../../store/schemaHooks';
import EntitlementsService from '../../services/EntitlementsService';
import {
    buildFormAttributesFromTier2Attributes,
    buildFormAttributesFromTier2AttributeBindings,
    isEntitySetFacade,
    type FormAttributeOption,
} from '../../types/EntityEntitlements';

interface SchemaAttributeSelectorProps {
    value: string | null;
    onChange: (value: string | null) => void;
    label?: string;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    description?: React.ReactNode;
    entityType?: string;
    filterReferenceOnly?: boolean;
}

export const SchemaAttributeSelector: React.FC<SchemaAttributeSelectorProps> = ({
    value,
    onChange,
    label = 'Attribute',
    required = false,
    disabled = false,
    error = false,
    description,
    entityType,
    filterReferenceOnly = false,
}) => {
    const [browserOpen, setBrowserOpen] = useState(false);
    const [tier2OptionsByEntityType, setTier2OptionsByEntityType] = useState<Record<string, FormAttributeOption[]>>({});
    const [tier2Loading, setTier2Loading] = useState(false);
    const fetchRequestedRef = useRef<Record<string, boolean>>({});

    const entityTypeFieldOptions = useFieldOptionsForEntityType(entityType || '');
    const normalizedEntityType = (entityType || '').trim().toLowerCase();

    useEffect(() => {
        if (!normalizedEntityType) return;
        if (entityTypeFieldOptions.length > 0) return;
        if (tier2OptionsByEntityType[normalizedEntityType] !== undefined) return;
        if (fetchRequestedRef.current[normalizedEntityType]) return;

        fetchRequestedRef.current[normalizedEntityType] = true;
        setTier2Loading(true);
        EntitlementsService.fetchEntityEntitlementForSchemaEntity(normalizedEntityType)
            .then((tier2) => {
                const options = isEntitySetFacade(tier2)
                    ? buildFormAttributesFromTier2Attributes(tier2?.Attributes)
                    : buildFormAttributesFromTier2AttributeBindings(tier2?.AttributeBindings)
                        ?? buildFormAttributesFromTier2Attributes(tier2?.Attributes);
                setTier2OptionsByEntityType((prev) => ({ ...prev, [normalizedEntityType]: options }));
            })
            .catch(() => {
                setTier2OptionsByEntityType((prev) => ({ ...prev, [normalizedEntityType]: [] }));
            })
            .finally(() => {
                setTier2Loading(false);
                delete fetchRequestedRef.current[normalizedEntityType];
            });
    }, [normalizedEntityType, entityTypeFieldOptions.length]);

    const fieldOptions = useMemo(() => {
        if (entityTypeFieldOptions.length > 0) return entityTypeFieldOptions;
        if (tier2OptionsByEntityType[normalizedEntityType] !== undefined) {
            return tier2OptionsByEntityType[normalizedEntityType];
        }
        return [];
    }, [entityTypeFieldOptions, tier2OptionsByEntityType, normalizedEntityType]);

    const attributes = useMemo(() => {
        if (!filterReferenceOnly) return fieldOptions;
        return fieldOptions.filter((option: FormAttributeOption) => {
            if (option.isReference === true) return true;
            const attrName = (option.name || option.id || '').toLowerCase();
            return attrName === 'id' || attrName === '$.id';
        });
    }, [fieldOptions, filterReferenceOnly]);

    const selectData = useMemo(() => {
        const options = attributes.map((attr) => {
            const id = attr.id || attr.name || '';
            const display = attr.label || attr.name || id;
            return { value: id, label: display };
        });
        if (value && !attributes.find(a => (a.id || a.name || '') === value)) {
            options.unshift({ value, label: value });
        }
        return options;
    }, [attributes, value]);

    const preloadedEntities = useMemo(
        () =>
            attributes.map((attr: FormAttributeOption) => {
                const attrId = attr.id || attr.name || '';
                const display = attr.label || attr.name || attrId;
                return { Id: attrId, DisplayName: display, Name: display, Type: 'SchemaAttribute' };
            }),
        [attributes]
    );

    const selectDisabled = disabled || !entityType || tier2Loading;
    const placeholder = !entityType ? 'Select a target set first' : 'Select an attribute...';

    const handleBrowserConfirm = (
        input: Array<{ Id: string; DisplayName?: string; EntityType?: string }> | string[]
    ) => {
        let selectedId: string | null = null;
        if (Array.isArray(input) && input.length > 0) {
            const first = input[0];
            selectedId = typeof first === 'object' && first !== null && 'Id' in first
                ? (first as { Id: string }).Id
                : String(first);
        }
        onChange(selectedId || null);
        setBrowserOpen(false);
    };

    return (
        <Box>
            <Group gap="xs" align="flex-end">
                <Select
                    flex={1}
                    label={label}
                    placeholder={placeholder}
                    data={selectData}
                    value={value}
                    onChange={(val) => onChange(val || null)}
                    required={required}
                    disabled={selectDisabled}
                    error={error}
                    searchable
                    clearable
                    rightSection={tier2Loading ? <Loader size={16} /> : undefined}
                />
                <Tooltip label={`Browse ${label}`} withArrow>
                    <ActionIcon
                        variant="light"
                        onClick={() => setBrowserOpen(true)}
                        disabled={selectDisabled}
                        mb={4}
                    >
                        <IconSearch size={16} />
                    </ActionIcon>
                </Tooltip>
            </Group>

            {description && (
                <Text size="xs" c="dimmed" display="block" mt={4}>
                    {description}
                </Text>
            )}

            <EntityBrowserDialog
                open={browserOpen}
                onClose={() => setBrowserOpen(false)}
                onConfirm={handleBrowserConfirm}
                entityType="SchemaAttribute"
                title={`Select ${label}`}
                callerName="SchemaAttributeSelector"
                preloadedEntities={preloadedEntities}
                singleSelection={true}
            />
        </Box>
    );
};
